import React from 'react';

import { Icon } from '../../assets/Icon/Icon';
import { VisuallyHidden } from '../../common/visually_hidden';
import { colors, darkThemeSelector, styled } from '../../stitches.config';

export type StateIconTone = 'positive' | 'negative' | 'attention' | 'neutral';

export type StateIconProps = {
  /**
   * Whether the feature is on: a check when enabled, a disable icon when not.
   */
  enabled: boolean;
  /**
   * Colours the icon for what the state means rather than whether it's on. Defaults to `positive`
   * when enabled and `neutral` when disabled; set `negative` or `attention` where the state is a
   * problem, such as a failed health check.
   */
  tone?: StateIconTone;
  /**
   * The state as read by screen readers, in place of "Enabled" or "Disabled", such as
   * "Not grounded".
   */
  label?: string;
};

const StateIconContainer = styled('span', {
  display: 'inline-flex',
  verticalAlign: 'middle',
  flexShrink: 0,
  padding: '$1',
  borderRadius: '9999px',

  variants: {
    tone: {
      positive: {
        backgroundColor: colors.tokenBgPositiveLight,
        [darkThemeSelector]: { backgroundColor: colors.tokenBgPositiveDark },
      },
      // Deeper than the negative and attention token backgrounds, which leave their icons
      // around 2:1 and 3:1 in dark mode; these get them to at least positive's 4:1.
      negative: {
        backgroundColor: colors.tokenBgNegativeLight,
        [darkThemeSelector]: { backgroundColor: colors.red900 },
      },
      attention: {
        backgroundColor: colors.tokenBgAttentionLight,
        [darkThemeSelector]: { backgroundColor: colors.amber800 },
      },
      neutral: {
        backgroundColor: colors.tokenBgNeutralLight,
        [darkThemeSelector]: { backgroundColor: colors.tokenBgNeutralDark },
      },
    },
    dimmed: {
      true: { opacity: 0.75 },
      false: {},
    },
  },
});
StateIconContainer.displayName = 'StateIconContainer';

export function StateIcon({
  enabled,
  label = enabled ? 'Enabled' : 'Disabled',
  tone = enabled ? 'positive' : 'neutral',
}: StateIconProps) {
  return (
    <>
      <StateIconContainer tone={tone} dimmed={!enabled && tone === 'neutral'}>
        <Icon
          icon={enabled ? 'checkmark-circle' : 'disable'}
          size={16}
          variant={tone}
          color={tone === 'negative' ? { dark: 'red200' } : undefined}
        />
      </StateIconContainer>
      <VisuallyHidden>{label}</VisuallyHidden>
    </>
  );
}
